/**
 * The modal shell every editor uses.
 *
 * Closes on Escape and on a click outside the panel; Ctrl/Cmd+Enter saves,
 * which is what the single-file build did and what people are used to.
 *
 * ## One way out, not two
 *
 * The X in the header closes it, so a panel with nothing to save has no
 * business also carrying a Close button in the footer: two controls for
 * one action, and the footer one sits among the buttons that change
 * something, where it reads as one of them. On a form the footer button
 * stays - there it is Cancel, the counterpart to Save, and abandoning
 * half-typed work is a different decision from putting a page away.
 *
 * `headActions` is for the one or two things that belong beside the X
 * rather than among the actions: reading the panel is not acting on it.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import Icon from '../lib/icons.jsx';

export default function Sheet({
  icon = 'plus', tone = 'g-purple', title, badge, onClose, onSave, saveLabel = 'Save',
  saveDisabled = false,
  closeLabel = 'Cancel', bodyClass = '', wide, compact, hero, footerLeft, headActions,
  children
}) {
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && onSave && !saveDisabled) onSave();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onSave, saveDisabled]);

  return (
    <div className="veil open" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className={`sheet${wide ? ' wide' : ''}${compact ? ' compact' : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="shead">
          <div className={`ic ${tone}`}><Icon name={icon} /></div>
          <h3>{title}</h3>
          {badge}
          {headActions ? <div className="shacts">{headActions}</div> : null}
          <button type="button" className="iconbtn" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>

        {hero}
        {/* The scroller is a plain block, not the grid itself: a grid that is
            also a shrinking flex item gets its rows squeezed and the panels
            collapse to their headers. */}
        <div className="sscroll">
          <div className={`form ${bodyClass}`.trimEnd()}>{children}</div>
        </div>

        {/* Nothing to save and nothing to do: the header X is the whole
            footer, so there is no footer. */}
        {onSave || footerLeft ? (
          <div className="sfoot">
            {/* See the docblock - a Cancel, only where there is a Save.
                The empty span holds the right-hand group in place. */}
            {onSave
              ? <button type="button" className="btn" onClick={onClose}>{closeLabel}</button>
              : <span />}
            <div style={{ display: 'flex', gap: 10 }}>
              {footerLeft}
              {onSave ? (
                <button type="button" className="btn primary" onClick={onSave} disabled={saveDisabled}>{saveLabel}</button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ form bits */

export const Group = ({ children }) => <div className="grp">{children}</div>;

export const Field = ({ label, wide, children, hint }) => (
  <div className={`f${wide ? ' wide' : ''}`}>
    <label>{label}</label>
    {children}
    {hint}
  </div>
);

export const QuickActions = ({ children }) => <div className="qk">{children}</div>;

/** A select built from a list of plain string options. */
/* `labels` renames the options without changing what is stored, for the
   places where the value is a key ('no_show') and the reader needs a
   sentence ('They did not turn up'). Positional, so it lines up with
   `options`. */
export const OptionSelect = ({ value, onChange, options, labels, blank, id }) => (
  <select id={id} value={value ?? ''} onChange={e => onChange(e.target.value)}>
    {blank !== undefined ? <option value="">{blank}</option> : null}
    {options.map((o, i) => <option key={o} value={o}>{labels ? labels[i] : o}</option>)}
  </select>
);

/** A select over records that have an id and a display name. */
/**
 * Pick a record by typing its name.
 *
 * A dropdown is fine for a handful of options, but the company and contact
 * lists grow with the business, and scrolling fifty-odd entries to find one is
 * slower than typing three letters. `list` + `datalist` gives native typeahead
 * inside an ordinary text input, so this looks and behaves exactly like the
 * Name and Email fields beside it and needs no styling of its own.
 */
/**
 * `onCreate(name)` — when given, a name that matches nothing becomes a new
 * record on the way out of the box (blur or Enter), and its id is selected.
 * Without it, typing a company that does not exist selected nothing, the
 * deal saved with no company, and the box read as broken.
 */
export function RecordSelect({ value, onChange, records, labelKey = 'name', blank = '—', onCreate }) {
  const listId = useId();

  // Two records can share a name, so the typed text has to identify one of
  // them unambiguously. Only the repeated names get a suffix; everything else
  // stays exactly as it reads.
  const labels = useMemo(() => {
    const times = {};
    records.forEach(r => {
      const n = String(r[labelKey] || '');
      times[n] = (times[n] || 0) + 1;
    });
    const map = new Map();
    records.forEach(r => {
      const n = String(r[labelKey] || '');
      const hint = r.city || r.email || r.web || String(r.id).slice(0, 4);
      map.set(r.id, times[n] > 1 && hint ? `${n} · ${hint}` : n);
    });
    return map;
  }, [records, labelKey]);

  // Seeded once: these sheets mount fresh for each record, so syncing back
  // from `value` on every render would only fight whoever is typing.
  const [text, setText] = useState(() => {
    const r = records.find(x => x.id === value);
    return r ? labels.get(r.id) || '' : '';
  });

  const find = t => {
    const wanted = t.trim().toLowerCase();
    return records.find(r => (labels.get(r.id) || '').toLowerCase() === wanted);
  };

  const commit = next => {
    setText(next);
    const hit = find(next);
    // Half-typed text selects nothing rather than guessing at a partial match:
    // silently picking the wrong company is worse than picking none.
    onChange(hit ? hit.id : '');
  };

  /* Leaving the box with a name nobody has: make the record, select it. */
  const settle = () => {
    const name = text.trim();
    if (!name || !onCreate || find(name)) return;
    const id = onCreate(name);
    if (id) onChange(id);
  };

  const unmatched = !!onCreate && !!text.trim() && !find(text);

  return (
    <>
      <input
        list={listId}
        value={text}
        placeholder={blank === '—' ? 'Type to search…' : blank}
        onChange={e => commit(e.target.value)}
        onBlur={settle}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); settle(); } }}
      />
      <datalist id={listId}>
        {records.map(r => <option key={r.id} value={labels.get(r.id)} />)}
      </datalist>
      {unmatched ? (
        <button type="button" className="rsadd" onClick={settle}>
          <Icon name="plus" />Add “{text.trim()}”
        </button>
      ) : null}
    </>
  );
}

/** The amber advisory strip inside the client sheet. */
export const Warning = ({ messages }) => (
  <div className={`warn${messages.length ? ' show' : ''}`}>
    <Icon name="alert" />
    <span>{messages.join(' ')}</span>
  </div>
);
